/**
 * StandaloneSecurityDashboard - Security features without requiring an event
 * For daily campus operations: visitors, deliveries, contractors, vehicles
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Shield, Users, Car, Camera, Building, LogIn, LogOut, Search, 
  Plus, UserPlus, Truck, Briefcase, Package, Clock, Calendar,
  QrCode, Printer, Download, Filter, RefreshCw, AlertCircle, Settings,
  MapPin, Edit2, Trash2, ArrowLeft, AlertTriangle, CheckCircle, ScanLine
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { useOpsRole } from '@/hooks/useOpsRole';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Import the feature components
import VehicleManagement from './VehicleManagement';
import VisitorPassManagement from './VisitorPassManagement';
import NetworkCameraSettings from './NetworkCameraSettings';
import LiveCameraViewer from './LiveCameraViewer';
import SecurityPatrols from './SecurityPatrols';
import SecurityRolesManagement from './SecurityRolesManagement';
import SecurityOfficersManagement from './SecurityOfficersManagement';
import CheckpointsManagement from './CheckpointsManagement';
import SafetyCenter from './SafetyCenter';
import ANPRPanel from './ANPRPanel';
import { Html5Qrcode } from 'html5-qrcode';
import { getToken } from '@/lib/authToken';

// Standalone Scanner Tab Component
const StandaloneScannerTab = ({ token, queryClient }) => {
  const [scanMode, setScanMode] = useState('entry'); // entry or exit
  const [selectedEventId, setSelectedEventId] = useState('');
  const [showCameraDialog, setShowCameraDialog] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [manualInput, setManualInput] = useState('');
  const [activeCamera, setActiveCamera] = useState(null);
  const html5QrRef = React.useRef(null);

  // Fetch available events
  const { data: events = [] } = useQuery({
    queryKey: ['scanner-events'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/admin/events`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) return [];
      return res.json();
    }
  });

  // Initialize camera
  React.useEffect(() => {
    Html5Qrcode.getCameras()
      .then(devices => {
        const backCamera = devices.find(d => 
          d.label.toLowerCase().includes('back') || 
          d.label.toLowerCase().includes('rear')
        );
        setActiveCamera(backCamera?.id || devices[0]?.id);
      })
      .catch(err => console.error('Camera error:', err));
    
    return () => stopScanning();
  }, []);

  const startScanning = async () => {
    if (!activeCamera) {
      toast.error('No camera available');
      return;
    }

    setShowCameraDialog(true);
    setLastResult(null);

    setTimeout(async () => {
      try {
        html5QrRef.current = new Html5Qrcode('standalone-scanner-camera');
        await html5QrRef.current.start(
          activeCamera,
          { fps: 15, qrbox: { width: 250, height: 250 } },
          handleScanSuccess,
          () => {}
        );
        setIsScanning(true);
      } catch (err) {
        console.error('Camera start error:', err);
        toast.error('Failed to start camera');
        setShowCameraDialog(false);
      }
    }, 300);
  };

  const stopScanning = async () => {
    if (html5QrRef.current) {
      try {
        if (html5QrRef.current.isScanning) {
          await html5QrRef.current.stop();
        }
        html5QrRef.current.clear();
        html5QrRef.current = null;
      } catch (e) {}
    }
    setIsScanning(false);
  };

  const closeCameraDialog = async () => {
    await stopScanning();
    setShowCameraDialog(false);
  };

  const handleScanSuccess = async (decodedText) => {
    if (isProcessing) return;
    setIsProcessing(true);
    
    if (html5QrRef.current) {
      try { await html5QrRef.current.pause(); } catch (e) {}
    }

    await processCode(decodedText);
  };

  const processCode = async (code) => {
    if (!code.trim()) {
      setIsProcessing(false);
      return;
    }

    // First try standalone visitor pass code
    let endpoint = scanMode === 'entry' 
      ? `${API_URL}/api/admin/campus/entry/scan`
      : `${API_URL}/api/admin/campus/exit/scan`;
    
    // If an event is selected, use event-specific endpoint
    if (selectedEventId) {
      endpoint = scanMode === 'entry'
        ? `${API_URL}/api/admin/events/${selectedEventId}/campus-entry/delegate/scan`
        : `${API_URL}/api/admin/events/${selectedEventId}/campus-exit/scan`;
    }

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ qr_data: code, pass_code: code })
      });

      const data = await response.json();

      if (response.ok && (data.success || data.status === 'success' || data.status === 'already_on_campus')) {
        setLastResult({
          success: true,
          message: data.message || (scanMode === 'entry' ? 'Entry recorded' : 'Exit recorded'),
          person: data.delegate?.name || data.visitor?.full_name || data.person_name || 'Person'
        });
        toast.success(data.message || 'Scan successful');
        queryClient.invalidateQueries(['standalone']);
        
        setTimeout(() => closeCameraDialog(), 1500);
      } else {
        setLastResult({
          success: false,
          message: data.detail || data.error || 'Scan failed'
        });
        toast.error(data.detail || 'Not found');
        
        // Resume scanning
        if (html5QrRef.current) {
          try { await html5QrRef.current.resume(); } catch (e) {}
        }
      }
    } catch (err) {
      setLastResult({ success: false, message: 'Network error' });
      toast.error('Network error');
      if (html5QrRef.current) {
        try { await html5QrRef.current.resume(); } catch (e) {}
      }
    } finally {
      setIsProcessing(false);
      setManualInput('');
    }
  };

  const handleManualSubmit = (e) => {
    e.preventDefault();
    if (manualInput.trim()) processCode(manualInput.trim());
  };

  return (
    <div className="space-y-6">
      {/* Mode Toggle */}
      <div className="flex flex-wrap gap-4 items-center">
        <div className="flex rounded-lg border overflow-hidden">
          <button
            onClick={() => setScanMode('entry')}
            className={`px-6 py-3 flex items-center gap-2 font-medium transition-colors ${
              scanMode === 'entry' 
                ? 'bg-green-600 text-white' 
                : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            <LogIn className="h-5 w-5" />
            Entry
          </button>
          <button
            onClick={() => setScanMode('exit')}
            className={`px-6 py-3 flex items-center gap-2 font-medium transition-colors ${
              scanMode === 'exit' 
                ? 'bg-red-600 text-white' 
                : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            <LogOut className="h-5 w-5" />
            Exit
          </button>
        </div>
        
        {/* Event Selector */}
        <Select value={selectedEventId || "all"} onValueChange={(v) => setSelectedEventId(v === "all" ? "" : v)}>
          <SelectTrigger className="w-64">
            <SelectValue placeholder="All Events (General Campus)" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Events (General Campus)</SelectItem>
            {events.map(event => (
              <SelectItem key={event.id} value={event.id}>
                {event.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Scanner Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Camera Scanner */}
        <Card className={`border-2 border-dashed ${scanMode === 'entry' ? 'border-green-300 bg-green-50/50' : 'border-red-300 bg-red-50/50'}`}>
          <CardHeader>
            <CardTitle className={`flex items-center gap-2 ${scanMode === 'entry' ? 'text-green-700' : 'text-red-700'}`}>
              <Camera className="h-5 w-5" />
              Camera Scanner
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button
              onClick={startScanning}
              className={`w-full h-20 text-lg ${scanMode === 'entry' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}
              disabled={!activeCamera}
            >
              <Camera className="h-6 w-6 mr-3" />
              Open Camera Scanner
            </Button>
            <p className="text-sm text-center text-muted-foreground">
              Tap to scan QR code or badge
            </p>
          </CardContent>
        </Card>

        {/* Manual Input */}
        <Card className="border-2 border-dashed border-blue-300 bg-blue-50/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-blue-700">
              <Search className="h-5 w-5" />
              Manual / Hardware Scanner
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleManualSubmit} className="space-y-4">
              <Input
                value={manualInput}
                onChange={(e) => setManualInput(e.target.value)}
                placeholder="Enter code or use hardware scanner..."
                className="h-12 text-lg"
                autoFocus
              />
              <Button
                type="submit"
                className={`w-full ${scanMode === 'entry' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}
                disabled={!manualInput.trim() || isProcessing}
              >
                {isProcessing ? (
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                ) : scanMode === 'entry' ? (
                  <LogIn className="h-4 w-4 mr-2" />
                ) : (
                  <LogOut className="h-4 w-4 mr-2" />
                )}
                Record {scanMode === 'entry' ? 'Entry' : 'Exit'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Camera Dialog */}
      <Dialog open={showCameraDialog} onOpenChange={closeCameraDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className={`flex items-center gap-2 ${scanMode === 'entry' ? 'text-green-700' : 'text-red-700'}`}>
              <QrCode className="h-5 w-5" />
              {scanMode === 'entry' ? 'Scan for Entry' : 'Scan for Exit'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div 
              id="standalone-scanner-camera"
              className="w-full aspect-square bg-black rounded-lg overflow-hidden"
            />
            
            {isScanning && !isProcessing && (
              <div className="text-center">
                <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${
                  scanMode === 'entry' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                }`}>
                  <QrCode className="h-4 w-4 animate-pulse" />
                  <span>Point camera at QR code</span>
                </div>
              </div>
            )}
            
            {isProcessing && (
              <div className="text-center">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-full">
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  <span>Processing...</span>
                </div>
              </div>
            )}

            {lastResult && (
              <div className={`p-4 rounded-lg text-center ${lastResult.success ? 'bg-green-50' : 'bg-red-50'}`}>
                <div className={`w-16 h-16 mx-auto mb-2 rounded-full flex items-center justify-center ${
                  lastResult.success ? 'bg-green-500' : 'bg-red-500'
                }`}>
                  {lastResult.success ? (
                    <LogIn className="h-10 w-10 text-white" />
                  ) : (
                    <AlertCircle className="h-10 w-10 text-white" />
                  )}
                </div>
                <p className={`font-bold text-xl ${lastResult.success ? 'text-green-700' : 'text-red-700'}`}>
                  {lastResult.success ? (scanMode === 'entry' ? 'ENTRY OK' : 'EXIT OK') : 'NOT FOUND'}
                </p>
                {lastResult.person && (
                  <p className="text-gray-600">{lastResult.person}</p>
                )}
                <p className="text-sm text-gray-500 mt-1">{lastResult.message}</p>
              </div>
            )}

            <Button onClick={closeCameraDialog} variant="outline" className="w-full">
              Close Scanner
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Incidents Overview Component for Security Dashboard
const IncidentsOverview = ({ token }) => {
  const [selectedEventId, setSelectedEventId] = useState('all');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  // Fetch events
  const { data: events = [] } = useQuery({
    queryKey: ['incidents-events'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/admin/events`, { headers });
      if (!res.ok) return [];
      return res.json();
    }
  });

  // Fetch incidents from all events or selected event
  const { data: incidents = [], isLoading, refetch } = useQuery({
    queryKey: ['all-incidents', selectedEventId],
    queryFn: async () => {
      if (selectedEventId === 'all') {
        // Fetch incidents from all events
        const allIncidents = [];
        for (const event of events) {
          try {
            const res = await fetch(`${API_URL}/api/admin/events/${event.id}/incidents`, { headers });
            if (res.ok) {
              const eventIncidents = await res.json();
              allIncidents.push(...eventIncidents.map(i => ({ ...i, event_name: event.name })));
            }
          } catch (e) {}
        }
        return allIncidents.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      } else {
        const res = await fetch(`${API_URL}/api/admin/events/${selectedEventId}/incidents`, { headers });
        if (!res.ok) return [];
        const event = events.find(e => e.id === selectedEventId);
        const data = await res.json();
        return data.map(i => ({ ...i, event_name: event?.name || 'Unknown' }));
      }
    },
    enabled: events.length > 0,
    refetchInterval: 30000
  });

  const severityColors = {
    low: 'bg-blue-100 text-blue-700',
    medium: 'bg-yellow-100 text-yellow-700',
    high: 'bg-orange-100 text-orange-700',
    critical: 'bg-red-100 text-red-700'
  };

  const statusColors = {
    open: 'bg-red-100 text-red-700',
    investigating: 'bg-blue-100 text-blue-700',
    resolved: 'bg-green-100 text-green-700'
  };

  const openCount = incidents.filter(i => i.status === 'open').length;
  const criticalCount = incidents.filter(i => i.severity === 'critical' && i.status !== 'resolved').length;

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className={criticalCount > 0 ? 'border-red-500 border-2' : ''}>
          <CardContent className="pt-4 text-center">
            <AlertTriangle className={`h-8 w-8 mx-auto mb-2 ${criticalCount > 0 ? 'text-red-500' : 'text-slate-400'}`} />
            <div className="text-2xl font-bold text-red-600">{criticalCount}</div>
            <div className="text-sm text-slate-500">Critical</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <Clock className="h-8 w-8 mx-auto mb-2 text-orange-500" />
            <div className="text-2xl font-bold">{openCount}</div>
            <div className="text-sm text-slate-500">Open</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <Shield className="h-8 w-8 mx-auto mb-2 text-blue-500" />
            <div className="text-2xl font-bold">{incidents.filter(i => i.status === 'investigating').length}</div>
            <div className="text-sm text-slate-500">Investigating</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <CheckCircle className="h-8 w-8 mx-auto mb-2 text-green-500" />
            <div className="text-2xl font-bold">{incidents.filter(i => i.status === 'resolved').length}</div>
            <div className="text-sm text-slate-500">Resolved</div>
          </CardContent>
        </Card>
      </div>

      {/* Event Filter */}
      <div className="flex gap-4 items-center">
        <Select value={selectedEventId} onValueChange={setSelectedEventId}>
          <SelectTrigger className="w-64">
            <SelectValue placeholder="All Events" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Events</SelectItem>
            {events.map(event => (
              <SelectItem key={event.id} value={event.id}>{event.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          <RefreshCw className="h-4 w-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Incidents List */}
      {isLoading ? (
        <div className="text-center py-8">Loading incidents...</div>
      ) : incidents.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Shield className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <p className="text-slate-600">No incidents reported</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {incidents.map((incident) => (
            <Card key={incident.id} className={incident.severity === 'critical' ? 'border-red-500 border-2' : ''}>
              <CardContent className="p-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-semibold">{incident.title}</h4>
                      <Badge className={severityColors[incident.severity] || severityColors.medium}>
                        {incident.severity}
                      </Badge>
                      <Badge className={statusColors[incident.status] || statusColors.open}>
                        {incident.status}
                      </Badge>
                    </div>
                    <p className="text-sm text-slate-600 line-clamp-2">{incident.description}</p>
                    <div className="flex gap-4 mt-2 text-xs text-slate-500">
                      <span>Event: {incident.event_name}</span>
                      {incident.location && <span>Location: {incident.location}</span>}
                      <span>{new Date(incident.created_at).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

const StandaloneSecurityDashboard = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showQuickVisitor, setShowQuickVisitor] = useState(false);
  const [showCameraSettings, setShowCameraSettings] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  // Role-gating — only chiefs/supervisors with security.manage can manage
  // cameras + patrols; guards see incidents/visitors/scan only.
  const { can, isSuper, loading: roleLoading } = useOpsRole('security');

  const token = localStorage.getItem('token') || localStorage.getItem('security_token') || getToken();
  
  // Get user info
  const securityUser = localStorage.getItem('security_user') ? JSON.parse(localStorage.getItem('security_user')) : null;
  const isSecurityOfficer = !!localStorage.getItem('security_token');

  // Logout handler
  const handleLogout = () => {
    localStorage.removeItem('security_token');
    localStorage.removeItem('security_user');
    localStorage.removeItem('token');
    localStorage.removeItem('eventflow_token');
    navigate('/security/login');
  };
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  // Fetch standalone campus stats
  const { data: stats = {}, isLoading: statsLoading } = useQuery({
    queryKey: ['standalone-campus-stats'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/admin/campus/stats`, { headers });
      if (!res.ok) return { today_visitors: 0, on_site: 0, today_vehicles: 0, pending_invites: 0 };
      return res.json();
    },
    refetchInterval: 30000 // Refresh every 30 seconds
  });

  // Fetch today's visitors
  const { data: todayVisitors = [], isLoading: visitorsLoading } = useQuery({
    queryKey: ['standalone-today-visitors'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/admin/campus/visitors/today`, { headers });
      if (!res.ok) return [];
      return res.json();
    },
    refetchInterval: 15000
  });

  // Fetch on-site people
  const { data: onSite = [], isLoading: onSiteLoading } = useQuery({
    queryKey: ['standalone-on-site'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/admin/campus/on-site`, { headers });
      if (!res.ok) return [];
      return res.json();
    },
    refetchInterval: 15000
  });

  // Fetch cameras for dashboard preview
  const { data: cameras = [] } = useQuery({
    queryKey: ['cameras-preview'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/cameras`, { headers });
      if (!res.ok) return [];
      return res.json();
    }
  });

  const activeCameras = cameras.filter(c => c.is_active);

  // Quick visitor registration
  const quickRegisterMutation = useMutation({
    mutationFn: async (data) => {
      const res = await fetch(`${API_URL}/api/admin/campus/visitors`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data)
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Registration failed');
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['standalone']);
      setShowQuickVisitor(false);
      toast.success(`Visitor registered! Pass: ${data.pass_code}`);
    },
    onError: (error) => toast.error(error.message)
  });

  // Check-out visitor
  const checkOutMutation = useMutation({
    mutationFn: async (visitorId) => {
      const res = await fetch(`${API_URL}/api/admin/campus/visitors/${visitorId}/check-out`, {
        method: 'POST',
        headers
      });
      if (!res.ok) throw new Error('Check-out failed');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['standalone']);
      toast.success('Visitor checked out');
    },
    onError: (error) => toast.error(error.message)
  });

  const purposeIcons = {
    meeting: <Briefcase className="h-4 w-4" />,
    delivery: <Package className="h-4 w-4" />,
    work: <Truck className="h-4 w-4" />,
    contractor: <Truck className="h-4 w-4" />,
    visitor: <Users className="h-4 w-4" />,
    interview: <Briefcase className="h-4 w-4" />,
    other: <Users className="h-4 w-4" />
  };

  const purposeColors = {
    meeting: 'bg-blue-100 text-blue-800',
    delivery: 'bg-yellow-100 text-yellow-800',
    work: 'bg-purple-100 text-purple-800',
    contractor: 'bg-orange-100 text-orange-800',
    visitor: 'bg-green-100 text-green-800',
    interview: 'bg-pink-100 text-pink-800',
    other: 'bg-gray-100 text-gray-800'
  };

  // Filter on-site by search
  const filteredOnSite = onSite.filter(v =>
    v.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    v.company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    v.pass_code?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-slate-50 p-6" data-testid="standalone-security-dashboard">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <Shield className="h-8 w-8 text-blue-600" />
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Security Dashboard</h1>
              {securityUser && (
                <p className="text-sm text-slate-500">Welcome, {securityUser.name || securityUser.email}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {!isSecurityOfficer && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => navigate('/admin')}
                data-testid="back-to-admin-btn"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Admin
              </Button>
            )}
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleLogout}
              data-testid="security-logout-btn"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
        <p className="text-slate-600">Manage campus visitors, vehicles, and access control</p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Today's Visitors</p>
                <p className="text-2xl font-bold">{stats.today_visitors || 0}</p>
              </div>
              <Users className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Currently On-Site</p>
                <p className="text-2xl font-bold text-green-600">{stats.on_site || onSite.length}</p>
              </div>
              <Building className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Vehicles Today</p>
                <p className="text-2xl font-bold text-purple-600">{stats.today_vehicles || 0}</p>
              </div>
              <Car className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Expected Today</p>
                <p className="text-2xl font-bold text-orange-600">{stats.pending_invites || 0}</p>
              </div>
              <Calendar className="h-8 w-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-8 w-full max-w-5xl" data-testid="security-tabs">
          <TabsTrigger value="dashboard" className="flex items-center gap-2" data-testid="security-tab-dashboard">
            <Shield className="h-4 w-4" />
            <span className="hidden sm:inline">Dashboard</span>
          </TabsTrigger>
          <TabsTrigger value="scan" className="flex items-center gap-2" data-testid="security-tab-scan">
            <QrCode className="h-4 w-4" />
            <span className="hidden sm:inline">Scan</span>
          </TabsTrigger>
          <TabsTrigger value="incidents" className="flex items-center gap-2" data-testid="security-tab-incidents">
            <AlertTriangle className="h-4 w-4" />
            <span className="hidden sm:inline">Incidents</span>
          </TabsTrigger>
          <TabsTrigger value="visitors" className="flex items-center gap-2" data-testid="security-tab-visitors">
            <Users className="h-4 w-4" />
            <span className="hidden sm:inline">Visitors</span>
          </TabsTrigger>
          <TabsTrigger value="vehicles" className="flex items-center gap-2" data-testid="security-tab-vehicles">
            <Car className="h-4 w-4" />
            <span className="hidden sm:inline">Vehicles</span>
          </TabsTrigger>
          {(isSuper || can('security.manage') || roleLoading) && (
            <TabsTrigger value="anpr" className="flex items-center gap-2" data-testid="security-tab-anpr">
              <ScanLine className="h-4 w-4" />
              <span className="hidden sm:inline">ANPR</span>
            </TabsTrigger>
          )}
          {(isSuper || can('security.manage') || roleLoading) && (
            <TabsTrigger value="patrols" className="flex items-center gap-2" data-testid="security-tab-patrols">
              <Shield className="h-4 w-4" />
              <span className="hidden sm:inline">Patrols</span>
            </TabsTrigger>
          )}
          {(isSuper || can('security.manage') || roleLoading) && (
            <TabsTrigger value="cameras" className="flex items-center gap-2" data-testid="security-tab-cameras">
              <Camera className="h-4 w-4" />
              <span className="hidden sm:inline">Cameras</span>
            </TabsTrigger>
          )}
        </TabsList>

        {/* Dashboard Tab - Quick Actions & On-Site List */}
        <TabsContent value="dashboard" className="space-y-6">
          {/* Quick Actions */}
          <div className="flex flex-wrap gap-3">
            <Button onClick={() => setShowQuickVisitor(true)} data-testid="quick-register-btn">
              <UserPlus className="h-4 w-4 mr-2" />
              Quick Register
            </Button>
            <Button variant="outline" onClick={() => setActiveTab('visitors')}>
              <Users className="h-4 w-4 mr-2" />
              Full Visitor Form
            </Button>
            <Button variant="outline" onClick={() => setActiveTab('vehicles')}>
              <Car className="h-4 w-4 mr-2" />
              Vehicle Check-in
            </Button>
            <Button variant="outline" onClick={() => setActiveTab('patrols')}>
              <Shield className="h-4 w-4 mr-2" />
              Start Patrol
            </Button>
          </div>

          {/* Currently On-Site */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Building className="h-5 w-5" />
                  Currently On-Site ({filteredOnSite.length})
                </CardTitle>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9 w-48"
                    />
                  </div>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => queryClient.invalidateQueries(['standalone'])}
                  >
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {onSiteLoading ? (
                <div className="text-center py-8 text-gray-500">Loading...</div>
              ) : filteredOnSite.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Building className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No visitors currently on site</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredOnSite.map((visitor) => (
                    <div 
                      key={visitor.id} 
                      className="flex items-center justify-between p-4 bg-white border rounded-lg hover:shadow-sm transition-shadow"
                      data-testid={`onsite-visitor-${visitor.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                          {purposeIcons[visitor.purpose] || <Users className="h-5 w-5 text-gray-400" />}
                        </div>
                        <div>
                          <p className="font-medium">{visitor.full_name}</p>
                          <div className="flex items-center gap-2 text-sm text-gray-500">
                            {visitor.company && <span>{visitor.company}</span>}
                            {visitor.visiting_person && (
                              <span>• Visiting: {visitor.visiting_person}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge className={purposeColors[visitor.purpose] || 'bg-gray-100'}>
                          {visitor.purpose}
                        </Badge>
                        <div className="text-right text-sm">
                          <p className="text-gray-500">
                            <Clock className="h-3 w-3 inline mr-1" />
                            {visitor.check_in_time ? new Date(visitor.check_in_time).toLocaleTimeString() : '-'}
                          </p>
                          <code className="text-xs bg-gray-100 px-1 rounded">{visitor.pass_code}</code>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => checkOutMutation.mutate(visitor.id)}
                          data-testid={`checkout-${visitor.id}`}
                        >
                          <LogOut className="h-4 w-4 mr-1" />
                          Out
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Activity */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Today's Activity
              </CardTitle>
            </CardHeader>
            <CardContent>
              {visitorsLoading ? (
                <div className="text-center py-4">Loading...</div>
              ) : todayVisitors.length === 0 ? (
                <div className="text-center py-4 text-gray-500">No visitors today</div>
              ) : (
                <div className="space-y-2">
                  {todayVisitors.slice(0, 10).map((visitor) => (
                    <div key={visitor.id} className="flex items-center justify-between py-2 border-b last:border-0">
                      <div className="flex items-center gap-3">
                        <span className="font-medium">{visitor.full_name}</span>
                        <Badge variant="outline" className="text-xs">
                          {visitor.purpose}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        {visitor.status === 'checked_in' ? (
                          <Badge className="bg-green-100 text-green-800">On Site</Badge>
                        ) : visitor.status === 'checked_out' ? (
                          <Badge className="bg-gray-100 text-gray-800">Left</Badge>
                        ) : (
                          <Badge className="bg-blue-100 text-blue-800">Registered</Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Camera Preview */}
          {activeCameras.length > 0 && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Camera className="h-5 w-5" />
                    Live Cameras ({activeCameras.length})
                  </CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => setActiveTab('cameras')}>
                    View All
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {activeCameras.slice(0, 4).map((camera) => (
                    <div 
                      key={camera.id} 
                      className="relative rounded-lg overflow-hidden bg-gray-900 cursor-pointer hover:ring-2 hover:ring-blue-500"
                      onClick={() => setActiveTab('cameras')}
                    >
                      {camera.stream_type === 'mjpeg' ? (
                        <img
                          src={camera.stream_url}
                          alt={camera.name}
                          className="w-full h-24 object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div className="hidden items-center justify-center h-24 text-gray-400">
                        <Camera className="h-6 w-6" />
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs p-1.5 truncate">
                        <div className="flex items-center gap-1">
                          <div className="h-1.5 w-1.5 rounded-full bg-green-500 animate-pulse" />
                          {camera.name}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Scan Tab - QR Scanner for Entry/Exit */}
        <TabsContent value="scan" className="space-y-6">
          <StandaloneScannerTab token={token} queryClient={queryClient} />
        </TabsContent>

        {/* Visitors Tab - Full Visitor Management */}
        <TabsContent value="visitors">
          <VisitorPassManagement eventId={null} />
        </TabsContent>

        {/* Vehicles Tab */}
        <TabsContent value="vehicles">
          <VehicleManagement eventId={null} />
        </TabsContent>

        {/* ANPR Tab */}
        <TabsContent value="anpr">
          <ANPRPanel />
        </TabsContent>

        {/* Incidents Tab */}
        <TabsContent value="incidents">
          <IncidentsOverview token={token} />
        </TabsContent>

        {/* Patrols Tab */}
        <TabsContent value="patrols">
          <SecurityPatrols />
        </TabsContent>

        {/* Cameras Tab */}
        <TabsContent value="cameras">
          <Tabs defaultValue="live">
            <TabsList className="mb-4">
              <TabsTrigger value="live">
                <Camera className="h-4 w-4 mr-2" />
                Live Feeds
              </TabsTrigger>
              <TabsTrigger value="settings">
                <Settings className="h-4 w-4 mr-2" />
                Camera Settings
              </TabsTrigger>
            </TabsList>
            <TabsContent value="live">
              <LiveCameraViewer onOpenSettings={() => setShowCameraSettings(true)} />
            </TabsContent>
            <TabsContent value="settings">
              <NetworkCameraSettings />
            </TabsContent>
          </Tabs>
        </TabsContent>

        {/* Settings Tab - Roles, Officers, Checkpoints */}
        <TabsContent value="settings">
          <Tabs defaultValue="roles">
            <TabsList className="mb-4">
              <TabsTrigger value="roles">
                <Shield className="h-4 w-4 mr-2" />
                Roles
              </TabsTrigger>
              <TabsTrigger value="officers">
                <Users className="h-4 w-4 mr-2" />
                Officers
              </TabsTrigger>
              <TabsTrigger value="checkpoints">
                <MapPin className="h-4 w-4 mr-2" />
                Checkpoints
              </TabsTrigger>
            </TabsList>
            <TabsContent value="roles">
              <SecurityRolesManagement />
            </TabsContent>
            <TabsContent value="officers">
              <SecurityOfficersManagement />
            </TabsContent>
            <TabsContent value="checkpoints">
              <CheckpointsManagement />
            </TabsContent>
          </Tabs>
        </TabsContent>
      </Tabs>

      {/* Quick Visitor Registration Dialog */}
      <Dialog open={showQuickVisitor} onOpenChange={setShowQuickVisitor}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Quick Visitor Registration</DialogTitle>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const form = new FormData(e.target);
              quickRegisterMutation.mutate({
                full_name: form.get('full_name'),
                company: form.get('company'),
                purpose: form.get('purpose'),
                visiting_person: form.get('visiting_person'),
                visiting_department: form.get('visiting_department'),
                phone: form.get('phone'),
                has_vehicle: form.get('has_vehicle') === 'yes',
                vehicle_plate: form.get('vehicle_plate')
              });
            }}
            className="space-y-4"
          >
            <div>
              <label className="text-sm font-medium">Full Name *</label>
              <Input name="full_name" required placeholder="John Doe" data-testid="input-quick-name" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Company</label>
                <Input name="company" placeholder="Company" />
              </div>
              <div>
                <label className="text-sm font-medium">Phone</label>
                <Input name="phone" placeholder="+1234567890" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Purpose *</label>
              <Select name="purpose" defaultValue="visitor">
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="meeting">Meeting</SelectItem>
                  <SelectItem value="delivery">Delivery</SelectItem>
                  <SelectItem value="contractor">Contractor</SelectItem>
                  <SelectItem value="work">Work/Maintenance</SelectItem>
                  <SelectItem value="interview">Interview</SelectItem>
                  <SelectItem value="visitor">General Visitor</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Visiting Person</label>
                <Input name="visiting_person" placeholder="Host name" />
              </div>
              <div>
                <label className="text-sm font-medium">Department</label>
                <Input name="visiting_department" placeholder="IT, HR..." />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Has Vehicle?</label>
                <Select name="has_vehicle" defaultValue="no">
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="no">No</SelectItem>
                    <SelectItem value="yes">Yes</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Vehicle Plate</label>
                <Input name="vehicle_plate" placeholder="ABC 123" />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowQuickVisitor(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={quickRegisterMutation.isPending} data-testid="submit-quick-register">
                {quickRegisterMutation.isPending ? 'Registering...' : 'Register & Print Pass'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StandaloneSecurityDashboard;
